import type {
  FilePasswordSessionEntry,
  FilesPasswordsSlice,
} from '../../interfaces/FilesPasswordsSlice';
import { ticksToMs } from '../../utils/utils';

/** Buffer before session expiry for in-flight requests; must stay below minimum session duration (1 min). */
const EXPIRATION_MARGIN_MS = 5 * 1000; // 5 seconds

/**
 * Zustand slice for file password sessions.
 * State is an array of { fileId, sessionToken, expiresAt } (one entry per verify).
 */
export const createFilesPasswordsSlice = (set: any, get: any): FilesPasswordsSlice => ({
  filePasswordSessions: [],
  pendingFilePasswordRequest: null,
  pendingFilePasswordSource: null,
  filePasswordLockoutInfo: null,
  fileIdToNavigateAfterPassword: null,
  pendingFilePasswordFromMoveForm: false,
  pendingFilePasswordFromUpload: false,
  pendingFilePasswordCancelledByUser: false,

  setFilePasswordSession: (entry: FilePasswordSessionEntry) => {
    set((state: FilesPasswordsSlice) => {
      const sessions = state.filePasswordSessions;
      const filtered = sessions.filter((e) => e.fileId !== entry.fileId);
      return { filePasswordSessions: [...filtered, entry] };
    });
  },

  getFilePasswordSession: (fileId) => {
    const state = get();
    const entry = state.filePasswordSessions.find(
      (e: FilePasswordSessionEntry) => e.fileId === fileId,
    );
    if (!entry) return null;

    const now = Date.now();
    const expiresAtMs = ticksToMs(entry.expiresAt);
    if (expiresAtMs < now + EXPIRATION_MARGIN_MS) {
      set((state: FilesPasswordsSlice) => ({
        filePasswordSessions: state.filePasswordSessions.filter((e) => e.fileId !== fileId),
      }));
      return null;
    }
    return entry.sessionToken;
  },

  getFilePasswordSessionInfo: (fileId) => {
    const state = get();
    const entry = state.filePasswordSessions.find(
      (e: FilePasswordSessionEntry) => e.fileId === fileId,
    );
    if (!entry) return null;

    const now = Date.now();
    const expiresAtMs = ticksToMs(entry.expiresAt);
    if (expiresAtMs < now + EXPIRATION_MARGIN_MS) {
      set((state: FilesPasswordsSlice) => ({
        filePasswordSessions: state.filePasswordSessions.filter((e) => e.fileId !== fileId),
      }));
      return null;
    }
    return { sessionToken: entry.sessionToken, expiresAt: entry.expiresAt };
  },

  getFilePasswordSessionHeaderValue: () => {
    const state = get();
    const now = Date.now();
    const valid = state.filePasswordSessions.filter((e: FilePasswordSessionEntry) => {
      const expiresAtMs = ticksToMs(e.expiresAt);
      return expiresAtMs >= now + EXPIRATION_MARGIN_MS;
    });
    const expired = state.filePasswordSessions.filter((e: FilePasswordSessionEntry) => {
      const expiresAtMs = ticksToMs(e.expiresAt);
      return expiresAtMs < now + EXPIRATION_MARGIN_MS;
    });
    if (expired.length > 0) {
      const expiredFileIds = new Set(expired.map((e: FilePasswordSessionEntry) => e.fileId));
      set((state: FilesPasswordsSlice) => ({
        filePasswordSessions: state.filePasswordSessions.filter(
          (e: FilePasswordSessionEntry) => !expiredFileIds.has(e.fileId),
        ),
      }));
    }
    if (valid.length === 0) return null;
    const dict: Record<string, string> = {};
    valid.forEach((e: FilePasswordSessionEntry) => {
      dict[String(e.fileId)] = e.sessionToken;
    });
    return JSON.stringify(dict);
  },

  removeFilePasswordSession: (fileId) => {
    set((state: FilesPasswordsSlice) => ({
      filePasswordSessions: state.filePasswordSessions.filter(
        (e: FilePasswordSessionEntry) => e.fileId !== fileId,
      ),
    }));
  },

  clearAllFilePasswordSessions: () => {
    set({ filePasswordSessions: [] });
  },

  /** Clears all file password state on logout (sessions, pending request, lockout, navigation, etc.) */
  resetFilePasswordsOnLogout: () => {
    set({
      filePasswordSessions: [],
      pendingFilePasswordRequest: null,
      pendingFilePasswordSource: null,
      filePasswordLockoutInfo: null,
      fileIdToNavigateAfterPassword: null,
      pendingFilePasswordFromMoveForm: false,
      pendingFilePasswordFromUpload: false,
      pendingFilePasswordCancelledByUser: false,
    });
  },

  isFilePasswordSessionValid: (fileId, marginMinutes = 1) => {
    const state = get();
    const entry = state.filePasswordSessions.find(
      (e: FilePasswordSessionEntry) => e.fileId === fileId,
    );
    if (!entry) return false;
    const now = Date.now();
    const expiresAtMs = ticksToMs(entry.expiresAt);
    const marginMs = marginMinutes * 60 * 1000;
    return expiresAtMs >= now + marginMs;
  },

  setPendingFilePasswordRequest: (request) => {
    set({ pendingFilePasswordRequest: request });
  },

  setPendingFilePasswordSource: (source) => {
    set({ pendingFilePasswordSource: source });
  },

  clearPendingFilePasswordRequest: () => {
    set({ pendingFilePasswordRequest: null, pendingFilePasswordSource: null });
  },

  setFileIdToNavigateAfterPassword: (fileId) => {
    set({ fileIdToNavigateAfterPassword: fileId });
  },

  setPendingFilePasswordFromMoveForm: (value) => {
    set({ pendingFilePasswordFromMoveForm: value });
  },

  setPendingFilePasswordFromUpload: (value) => {
    set({ pendingFilePasswordFromUpload: value });
  },

  setPendingFilePasswordCancelledByUser: (value) => {
    set({ pendingFilePasswordCancelledByUser: value });
  },

  setFilePasswordLockoutInfo: (fileId, minutesRemaining) => {
    set({ filePasswordLockoutInfo: { fileId, minutesRemaining } });
  },

  clearFilePasswordLockoutInfo: () => {
    set({ filePasswordLockoutInfo: null });
  },
});
